import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Check, Link2, Loader2, PauseCircle, RefreshCw, TriangleAlert } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { getHoursChecks, refreshHoursCheck, setHoursSync, type HoursCheckDto } from '../../api/brands';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { getWeekdayLabels, maskBits } from './weekdays';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

/** Плашки под графиком работы (Алексей 07.10, #1284): «Совпадает с сайтом»,
 *  «Совпадает с Яндекс Картами», «Совпадает с 2ГИС» — по источникам, которые есть у
 *  филиала (сайт сети через плагин сети, карты через подключённые отзывы). Маленькая
 *  иконка refresh пересчитывает сейчас; ночью считает бэкенд. Ничего нет — ничего не рисуем.
 *  Переключатель «Не синхронизировать часы с сайтом и картами» (Алексей 27.10): ночь перестаёт
 *  ставить расписание как на сайте, плашки расхождения остаются, «Статус» показывает проблему. */

function dayRange(bits: number[], labels: string[]): string {
  if (!bits.length) return '';
  const runs: number[][] = [];
  let run = [bits[0]];
  for (let i = 1; i < bits.length; i++) {
    if (bits[i] === bits[i - 1] + 1) run.push(bits[i]);
    else { runs.push(run); run = [bits[i]]; }
  }
  runs.push(run);
  return runs.map(r => (r.length >= 2 ? `${labels[r[0]]}–${labels[r[r.length - 1]]}` : labels[r[0]])).join(', ');
}

function title(c: HoursCheckDto, labels: string[], lang: 'ru' | 'en'): string {
  const when = c.checked_at ? new Date(c.checked_at).toLocaleString(lang === 'en' ? 'en-GB' : 'ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const src = c.rows.map(r => `${dayRange(maskBits(r.weekdays_mask), labels)} ${r.open_time.slice(0, 5)}–${r.close_time.slice(0, 5)}`).join('; ');
  const head = c.error ? c.error : src;
  return [head, when ? `(${lang === 'en' ? 'checked' : 'проверено'} ${when})` : ''].filter(Boolean).join(' ');
}

const HoursCheckChips: React.FC<{ zoneId: string }> = ({ zoneId }) => {
  const { t, lang } = useT();
  const labels = getWeekdayLabels(lang);
  const { data, mutate } = useSWR(`hours-checks:${zoneId}`, async () => (await getHoursChecks(zoneId)).data, { revalidateOnFocus: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);   // черновик причины; null — как на сервере
  const [syncBusy, setSyncBusy] = useState(false);

  const linkable = data?.linkable ?? [];
  if (!data || (data.checks.length === 0 && linkable.length === 0)) return null;

  async function refresh(source: string) {
    setBusy(source);
    try { const r = await refreshHoursCheck(zoneId, source); await mutate(r.data, { revalidate: false }); }
    catch { /* плашка останется прежней; ошибка видна по следующей ночной проверке */ }
    finally { setBusy(null); }
  }

  async function saveSync(body: { disabled?: boolean; note?: string }) {
    setSyncBusy(true);
    try { const r = await setHoursSync(zoneId, body); await mutate(r.data, { revalidate: false }); setNote(null); }
    catch { /* переключатель вернётся к серверному значению */ await mutate(); }
    finally { setSyncBusy(false); }
  }

  const syncOff = !!data.sync_disabled;
  const noteValue = note ?? data.sync_note ?? '';

  return (
    <>
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {data.checks.map(c => {
        const ok = c.status === 'match';
        const bad = c.status === 'mismatch' || c.status === 'ours_missing';
        // Имя источника в творительном падеже — «с сайтом», «с Яндекс Картами», «с 2ГИС».
        const known: Record<string, string> = { brand_site: t('hours.src.site'), yandex: t('hours.src.yandex'), '2gis': t('hours.src.2gis') };
        const name = known[c.source] ?? c.name;
        const text = ok ? t('hours.match', { name })
          : bad ? t('hours.mismatch', { name })
          : t('hours.unreadable', { name });
        return (
          <Tooltip key={c.source} content={title(c, labels, lang)}><span
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
              ok && 'border-emerald-300 bg-emerald-50 text-emerald-800',
              bad && 'border-amber-300 bg-amber-50 text-amber-800',
              !ok && !bad && 'border-border bg-muted text-muted-foreground',
            )}
          >
            {ok ? <Check className="size-3" /> : <TriangleAlert className="size-3" />}
            {c.source_url ? <a href={c.source_url} target="_blank" rel="noreferrer" className="hover:underline">{text}</a> : text}
            <button
              type="button"
              aria-label={t('hours.recheck')}
              disabled={busy === c.source}
              onClick={() => refresh(c.source)}
              className="ml-0.5 rounded p-0.5 opacity-60 hover:opacity-100 disabled:opacity-40"
            >
              {busy === c.source ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
            </button>
          </span></Tooltip>
        );
      })}
      {linkable.map(l => {
        const nom: Record<string, string> = { brand_site: t('hours.nom.site'), yandex: t('hours.nom.yandex'), '2gis': t('hours.nom.2gis') };
        return (
          <Link
            key={`link-${l.source}`}
            to={l.to}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
          >
            <Link2 className="size-3" />
            {t('hours.link', { name: nom[l.source] ?? l.name })}
          </Link>
        );
      })}
      {syncOff && data.checks.length > 0 && (
        <Tooltip content={data.sync_note || undefined}><span
          className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
        >
          <PauseCircle className="size-3" />
          {t('hours.sync.offChip')}
        </span></Tooltip>
      )}
    </div>
    {data.can_edit && data.checks.length > 0 && (
      <div className="mt-2 flex flex-col gap-1.5">
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={syncOff}
            disabled={syncBusy}
            onCheckedChange={(v) => saveSync({ disabled: v })}
            aria-label={t('hours.sync.off')}
          />
          {t('hours.sync.off')}
          {syncBusy && <Loader2 className="size-3 animate-spin" />}
        </label>
        {syncOff && (
          <Input
            className="h-8 max-w-md text-xs"
            maxLength={300}
            placeholder={t('hours.sync.notePlaceholder')}
            value={noteValue}
            disabled={syncBusy}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => { if (note !== null && note.trim() !== (data.sync_note ?? '')) void saveSync({ note }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          />
        )}
      </div>
    )}
    </>
  );
};

export default HoursCheckChips;
